import React, { useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Select from 'react-select'
import { selectCustomStyles } from '../../../../utils/Select'
import Toast, { ToastType } from '../../../../utils/toast'
import {
  useAddInvestorToUserMutation,
  useGetAllFundsOfUserQuery,
} from '../../../../generated/graphql'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'

interface AddFundModalProps {
  userId: string
  onClose: () => void
  onSuccess: () => void
}

const AddFundSchema = Yup.object().shape({
  fundname: Yup.string()
    .min(2, 'Fund name must be at least 2 characters')
    .max(100, 'Fund name must be less than 100 characters')
    .required('Fund name is required'),
  fundType: Yup.string().required('Fund type is required'),
})

const AddFundModal: React.FC<AddFundModalProps> = ({ userId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const addFundMutation = useAddInvestorToUserMutation<Error>(graphqlRequestClient, {
    onSuccess: () => {
      setLoading(false)
      Toast('Fund added successfully', ToastType.success)

      // Invalidate the funds query to refresh the list
      queryClient.invalidateQueries(useGetAllFundsOfUserQuery.getKey({ input: userId! }))

      onSuccess()
    },
    onError: (error: any) => {
      setLoading(false)
      const errorMessage =
        error?.response?.errors?.[0]?.message || error?.message || 'Failed to add fund'
      Toast(errorMessage, ToastType.error)
    },
  })

  const investorTypes: string[] = [
    'ANGEL',
    'VENTURE CAPITAL (VC)',
    'INCUBATOR',
    'ANGEL FUND',
    'ANGEL NETWORK',
    'CORPORATE',
    'FAMILY OFFICE',
    'PRIVATE EQUITY (PE)',
    'ACCELERATOR',
    'FINANCIAL INSTITUTION',
  ]

  const investorTypesOptions = investorTypes.map((type) => ({
    label: type,
    value: type,
  }))

  const formik = useFormik({
    initialValues: {
      fundname: '',
      fundType: '',
    },
    validationSchema: AddFundSchema,
    onSubmit: async (values) => {
      setLoading(true)

      addFundMutation.mutate({
        input: {
          userId: userId!,
          fundName: values.fundname?.trim(),
          fundType: values.fundType,
        },
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    formik.handleSubmit()
  }

  if (!userId) {
    return (
      <div
        className='modal fade show d-block'
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      >
        <div className='modal-dialog modal-dialog-centered' onClick={(e) => e.stopPropagation()}>
          <div className='modal-content'>
            <div className='modal-header'>
              <h3 className='modal-title fw-bold'>Add Fund</h3>
              <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
            </div>
            <div className='modal-body'>
              <div className='alert alert-danger'>
                Please select a user first before adding a fund.
              </div>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light h-38px' onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div
      className='modal fade show d-block'
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div className='modal-dialog modal-dialog-centered' onClick={(e) => e.stopPropagation()}>
        <div className='modal-content'>
          <div className='modal-header'>
            <h3 className='modal-title fw-bold'>Add Fund</h3>
            <button type='button' className='btn-close' onClick={onClose} aria-label='Close' />
          </div>

          <div className='modal-body'>
            <div className='mb-5'>
              <label className='form-label required fw-semibold'>Fund Name</label>
              <input
                className='form-control h-38px'
                type='text'
                autoComplete='off'
                placeholder='Enter fund name'
                {...formik.getFieldProps('fundname')}
              />
              {formik.touched.fundname && formik.errors.fundname && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block text-danger'>{formik.errors.fundname}</div>
                </div>
              )}
            </div>

            <div className='mb-5'>
              <label className='form-label required fw-semibold'>Fund Type</label>
              <Select
                options={investorTypesOptions}
                styles={selectCustomStyles}
                placeholder='Select fund type'
                onChange={(option: any) => {
                  formik.setFieldValue('fundType', option?.value || '')
                }}
                onBlur={() => formik.setFieldTouched('fundType', true)}
              />
              {formik.touched.fundType && formik.errors.fundType && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block text-danger'>{formik.errors.fundType}</div>
                </div>
              )}
            </div>
          </div>

          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light h-38px'
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary h-38px'
              onClick={handleSubmit}
              disabled={loading}
            >
              {!loading && <span className='fw-bold'>Add Fund</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddFundModal
